import { type ReactNode, useEffect } from "react";
import { BrowserRouter, Navigate, Route, Routes, useLocation } from "react-router";
import { FeedbackDialog } from "@/components/FeedbackDialog";
import { WelcomeModal } from "@/components/WelcomeModal";
import Login from "@/pages/auth/Login";
import MagicLanding from "@/pages/auth/MagicLanding";
import { Recover, ResetPassword } from "@/pages/auth/Recover";
import Register from "@/pages/auth/Register";
import BoardPage from "@/pages/board/BoardPage";
import Guide from "@/pages/Guide";
import Home from "@/pages/Home";
import Invite from "@/pages/Invite";
import PrintPage from "@/pages/PrintPage";
import Reports from "@/pages/Reports";
import Usage from "@/pages/Usage";
import Settings from "@/pages/Settings";
import SharePage from "@/pages/SharePage";
import Trash from "@/pages/Trash";
import Team from "@/pages/team/Team";
import { setUsageEnabled } from "@/lib/usage";
import { SessionProvider, useSession } from "./session";

function Private({ children }: { children: ReactNode }) {
  const { me, loading } = useSession();
  const loc = useLocation();
  useEffect(() => setUsageEnabled(!!me?.workspace.usageTracking), [me]);
  if (loading) return null;
  if (!me) return <Navigate to={`/login?next=${encodeURIComponent(loc.pathname + loc.search)}`} replace />;
  return (
    <>
      {children}
      <WelcomeModal />
      <FeedbackDialog />
    </>
  );
}

function Staff({ children }: { children: ReactNode }) {
  const { me } = useSession();
  if (!me?.staffRole) return <Navigate to="/" replace />;
  return children;
}

export default function App() {
  return (
    <SessionProvider>
      <BrowserRouter>
        <Routes>
          <Route path="/login" element={<Login />} />
          <Route path="/registro" element={<Register />} />
          <Route path="/entrar" element={<MagicLanding />} />
          <Route path="/recuperar" element={<Recover />} />
          <Route path="/restablecer" element={<ResetPassword />} />
          <Route path="/invitacion/:token" element={<Invite />} />
          <Route path="/s/:token" element={<SharePage />} />
          <Route path="/print/:id" element={<PrintPage />} />
          <Route
            path="/"
            element={
              <Private>
                <Home />
              </Private>
            }
          />
          <Route
            path="/b/:id"
            element={
              <Private>
                <BoardPage />
              </Private>
            }
          />
          <Route
            path="/guia"
            element={
              <Private>
                <Guide />
              </Private>
            }
          />
          <Route
            path="/ajustes"
            element={
              <Private>
                <Settings />
              </Private>
            }
          />
          <Route
            path="/equipo"
            element={
              <Private>
                <Staff>
                  <Team />
                </Staff>
              </Private>
            }
          />
          <Route
            path="/reportes"
            element={
              <Private>
                <Staff>
                  <Reports />
                </Staff>
              </Private>
            }
          />
          <Route
            path="/uso"
            element={
              <Private>
                <Staff>
                  <Usage />
                </Staff>
              </Private>
            }
          />
          <Route
            path="/papelera"
            element={
              <Private>
                <Staff>
                  <Trash />
                </Staff>
              </Private>
            }
          />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </BrowserRouter>
    </SessionProvider>
  );
}
